/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useRef } from "react";
import Input from "../ui/input";
import TextArea from "../ui/textarea";
import Button from "../ui/button";
import { useForm, SubmitHandler } from "react-hook-form";
import parsePhoneNumber from "libphonenumber-js";
import config from "@/config";
import ReCAPTCHA from "react-google-recaptcha";
import axios from 'axios';
import { navigate } from 'gatsby';

interface ContactFormProps extends React.HTMLProps<HTMLDivElement> {
  callBack: () => void;
}

interface IFormInput {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ callBack }) => {
  const {
    handleSubmit,
    register,
    formState: { errors }
  } = useForm<IFormInput>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const onSubmit: SubmitHandler<IFormInput> = async (data: IFormInput) => {
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    const captchaValue = recaptchaRef.current?.getValue();
    console.log("captchaValue", captchaValue)
    if (!captchaValue) {
      setSubmitError("Please complete the reCAPTCHA");
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append("Name", data.name);
    formData.append("Email", data.email);
    formData.append("Phone Number", data.phone);
    formData.append("Company", data.company);
    formData.append("Message", data.message);
    formData.append("g-recaptcha-response", captchaValue);

    try {
      const verifyResponse = await axios.post(`https://${config.RECAPTCHA_SITE_SECRET_URL}/verify`, {
        captchaValue: captchaValue
      });

      if (verifyResponse.data.success) {
        const formResponse = await fetch(`https://formkeep.com/f/${config.GATSBY_CONTACT_FORM_KEEP_KEY}`, {
          method: "POST",
          body: formData
        });
        if (!formResponse.ok) {
          throw new Error("Network response was not ok");
        }
        await formResponse.json();
        setSubmitSuccess("Form submitted successfully!");
        navigate('/thank-you');
      } else {
        setSubmitError("reCAPTCHA validation failed!");
      }
    } catch (error) {
      setSubmitError("Form submit failed, try again.");
      console.error("There was a problem with the fetch operation:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const phoneNumberValidation = (value: string) => {
    try {
      const phoneNumber = parsePhoneNumber(value);
      if (phoneNumber && phoneNumber.isValid()) {
        return true;
      }
      return "Enter valid phone number with country code";
    } catch (error) {
      return "Invalid phone number";
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mt-6">
        <Input
          register={register}
          name="name"
          placeholder="Full Name"
          required
          errors={errors.name}
        />
      </div>
      <div className="mt-6">
        <Input
          type="email"
          name="email"
          placeholder="Email"
          register={register}
          required
          validationOptions={{
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address"
            }
          }}
          errors={errors.email}
        />
      </div>
      <div className="mt-6">
        <Input
          type="tel"
          name="phone"
          placeholder="Phone number"
          register={register}
          required
          validationOptions={{
            validate: phoneNumberValidation
          }}
          errors={errors.phone}
        />
      </div>
      <div className="mt-6">
        <Input
          type="text"
          name="company"
          placeholder="Company name"
          register={register}
          required
          errors={errors.company}
        />
      </div>
      <div className="mt-6">
        <TextArea
          placeholder="Tell us about the project (scope, timeline, budget)"
          name="message"
          register={register}
          required
          errors={errors.message}
        />
      </div>
      <div className="mt-6">
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={config.RECAPTCHA_SITE_KEY}
        />
      </div>
      <div className="mt-6">
        <Button type="submit" size="lg">
          {isSubmitting ? "Submitting..." : "Let's talk"}
        </Button>
      </div>
      {submitError && (
        <div className="mt-6 text-red-500">
          {submitError}
        </div>
      )}
      {submitSuccess && (
        <div className="mt-6 text-green-500">
          {submitSuccess}
        </div>
      )}
    </form>
  );
};

export default ContactForm;